/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useMemo, useCallback, ReactNode, useDeferredValue } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DispatchDeliveryOrdersService, DispatchDeliveryOrderResponseDto } from '@/api/portal'
import { DdoColumn, SortConfig, SortDirection } from '@/pages/Orders/types'
import { ddoColumnsConfig } from '@/pages/Orders/config/columns.config'
import { parseDateLocal } from '@/lib/utils'

// Normalize string for location filtering - keep only letters and digits
const normalizeForSearch = (str: string): string => str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()

// Location columns that need normalization
const locationColumns = new Set([DdoColumn.pickupLocation, DdoColumn.deliveryLocation, DdoColumn.returnLocation])

export interface OrdersContextValue {
    // Data
    orders: DispatchDeliveryOrderResponseDto[]
    filteredOrders: DispatchDeliveryOrderResponseDto[]
    isLoading: boolean
    isRefetching: boolean
    lastUpdated?: Date

    // Filters
    filters: Partial<Record<DdoColumn, string | string[]>>
    activeFilters: [string, string | string[]][]
    setFilters: (filters: Partial<Record<DdoColumn, string | string[]>>) => void
    handleFilterChange: (field: DdoColumn, value: string | string[]) => void
    handleClearFilter: (field: DdoColumn) => void
    handleClearAllFilters: () => void
    getFilterDisplayValue: (key: string, value: string | string[]) => string

    // Sorting
    sortConfig: SortConfig
    handleSortAsc: (column: DdoColumn) => void
    handleSortDesc: (column: DdoColumn) => void

    // Actions
    handleRefresh: () => void
}

export const OrdersContext = createContext<OrdersContextValue | undefined>(undefined)

interface OrdersProviderProps {
    children: ReactNode
}

export const OrdersProvider = ({ children }: OrdersProviderProps) => {
    const [filters, setFilters] = useState<Partial<Record<DdoColumn, string | string[]>>>({})
    const [lastUpdated, setLastUpdated] = useState<Date>()
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        column: DdoColumn.orderNumber,
        direction: SortDirection.DESC,
    })

    // Use deferred value for filters to avoid blocking UI during typing
    const deferredFilters = useDeferredValue(filters)

    // Use React Query to fetch and cache orders
    const {
        data: orders = [],
        isLoading,
        isRefetching,
        refetch,
    } = useQuery({
        queryKey: ['orders'],
        queryFn: async () => {
            const response = await DispatchDeliveryOrdersService.dispatchDeliveryOrderControllerGetOrdersByCustomer()
            setLastUpdated(new Date())
            return response || []
        },
        refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
        refetchIntervalInBackground: false, // Don't refetch when tab is inactive
    })

    const handleRefresh = useCallback(() => {
        refetch()
    }, [refetch])

    const handleFilterChange = useCallback((field: DdoColumn, value: string | string[]) => {
        setFilters(prev => ({ ...prev, [field]: value }))
    }, [])

    const handleClearFilter = useCallback((field: DdoColumn) => {
        setFilters(prev => {
            const newFilters = { ...prev }
            delete newFilters[field]
            return newFilters
        })
    }, [])

    const handleClearAllFilters = useCallback(() => {
        setFilters({})
    }, [])

    const handleSortAsc = useCallback((column: DdoColumn) => {
        setSortConfig({ column, direction: SortDirection.ASC })
    }, [])

    const handleSortDesc = useCallback((column: DdoColumn) => {
        setSortConfig({ column, direction: SortDirection.DESC })
    }, [])

    const activeFilters = useMemo(() => {
        return Object.entries(filters).filter(([, value]) => {
            if (Array.isArray(value)) {
                return value.length > 0
            }
            return value !== '' && value !== null && value !== undefined
        })
    }, [filters])

    const getFilterDisplayValue = useCallback((key: string, value: string | string[]) => {
        const column = ddoColumnsConfig.find(col => col.key === key)
        if (!column) return String(value)

        if (Array.isArray(value)) {
            // For multi-text filters, show the items
            if (column.isMultiText) {
                return value.join(', ')
            }
            // For multi-select-group, calculate count like the component does
            if (column.isMultiSelectGroup && column.options) {
                let count = 0
                column.options.forEach(group => {
                    if (group.expandable) {
                        const selectedInGroup = group.options.filter(opt => value.includes(opt.value)).length
                        count += selectedInGroup
                    } else {
                        const hasSelected = group.options.some(opt => value.includes(opt.value))
                        if (hasSelected) {
                            count += 1
                        }
                    }
                })
                return `${count} selected`
            }
            // For multi-select, show count
            return `${value.length} selected`
        }

        if (column.isDate && value) {
            if (value === 'empty') {
                return 'Empty dates'
            }
            const date = parseDateLocal(value)
            return date.toLocaleDateString()
        }

        return String(value)
    }, [])

    // Helper function to sort orders
    const sortOrders = (
        ordersToSort: DispatchDeliveryOrderResponseDto[],
        config: SortConfig,
    ): DispatchDeliveryOrderResponseDto[] => {
        // Default sorting by order number descending if no sort is applied
        if (!config.column || !config.direction) {
            return ordersToSort.sort((a, b) => {
                const numA = Number(a?.number || 0)
                const numB = Number(b?.number || 0)
                return numB - numA
            })
        }

        const columnConfig = ddoColumnsConfig.find(col => col.key === config.column)
        if (!columnConfig) return ordersToSort

        return ordersToSort.sort((a, b) => {
            const valueA = columnConfig.path(a)
            const valueB = columnConfig.path(b)

            // Handle null/undefined values - push them to the end
            if (valueA == null && valueB == null) return 0
            if (valueA == null) return 1
            if (valueB == null) return -1

            let comparison = 0

            // For dates, parse and compare
            if (columnConfig.isDate) {
                const dateA = parseDateLocal(valueA)
                const dateB = parseDateLocal(valueB)
                comparison = dateA.getTime() - dateB.getTime()
            }
            // For numeric values
            else if (columnConfig.isNumeric) {
                const numA = Number(valueA)
                const numB = Number(valueB)
                comparison = numA - numB
            }
            // For strings
            else {
                const strA = String(valueA).toLowerCase()
                const strB = String(valueB).toLowerCase()
                comparison = strA.localeCompare(strB)
            }

            return config.direction === SortDirection.ASC ? comparison : -comparison
        })
    }

    const filteredOrders = useMemo(() => {
        if (!Array.isArray(orders)) {
            console.error('Orders is not an array:', orders)
            return []
        }

        try {
            // Early return if no filters
            if (Object.keys(deferredFilters).length === 0) {
                const sorted = sortOrders([...orders], sortConfig)
                return sorted
            }

            // Pre-compute active filters with their column configs to avoid repeated lookups
            const activeFilterConfigs = ddoColumnsConfig
                .map(column => {
                    const filterValue = deferredFilters[column.key]
                    if (!filterValue) return null
                    if (Array.isArray(filterValue) && filterValue.length === 0) return null

                    // Pre-process filter values for performance
                    let processedFilter = filterValue
                    if (typeof filterValue === 'string') {
                        processedFilter = filterValue.trim().toLowerCase()
                    } else if (Array.isArray(filterValue) && column.isMultiText) {
                        // Pre-lowercase and trim all filter items for multi-text filters
                        processedFilter = filterValue.map(item => item.trim().toLowerCase())
                    }

                    return {
                        column,
                        filterValue: processedFilter,
                        originalValue: filterValue,
                    }
                })
                .filter(Boolean)

            // If no active filters after processing, return sorted orders
            if (activeFilterConfigs.length === 0) {
                const sorted = sortOrders([...orders], sortConfig)
                return sorted
            }

            // Filter orders
            const filtered = orders.filter(order => {
                if (!order) return false

                // Check all active filters
                for (const config of activeFilterConfigs) {
                    if (!config) continue

                    const { column, filterValue, originalValue } = config

                    try {
                        const cellValue = column.path(order)

                        // For date columns, compare only the date part (YYYY-MM-DD) or check for empty
                        if (column.isDate) {
                            // Check for empty date filter
                            if (originalValue === 'empty') {
                                // Filter for null/undefined values (no date)
                                if (cellValue != null) return false
                            } else {
                                // Normal date comparison - skip if no value
                                if (cellValue == null) return false
                                const cellDate = parseDateLocal(cellValue)
                                // Format as YYYY-MM-DD for comparison
                                const cellDateString = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`
                                if (cellDateString !== originalValue) return false
                            }
                            continue
                        }

                        // For non-date columns, skip if no value
                        if (cellValue == null) return false

                        // For multi-text filters (OR logic - match any substring)
                        if (column.isMultiText && Array.isArray(filterValue)) {
                            const cellValueLower = String(cellValue).toLowerCase()
                            let matches = filterValue.some(filterItem => cellValueLower.includes(filterItem))

                            // If no match and extraPath exists, try alternative value
                            if (!matches && column.extraPath) {
                                const altValue = column.extraPath(order)
                                if (altValue != null) {
                                    const altValueLower = String(altValue).toLowerCase()
                                    matches = filterValue.some(filterItem => altValueLower.includes(filterItem))
                                }
                            }

                            if (!matches) return false
                            continue
                        }

                        // For multi-select columns
                        if (column.isMultiSelect && Array.isArray(originalValue)) {
                            if (!originalValue.includes(String(cellValue))) return false
                            continue
                        }

                        // For multi-select-group columns
                        if (column.isMultiSelectGroup && Array.isArray(originalValue)) {
                            if (!originalValue.includes(String(cellValue))) return false
                            continue
                        }

                        // Default string comparison
                        const isLocationColumn = locationColumns.has(column.key)
                        const cellValueLower = isLocationColumn
                            ? normalizeForSearch(String(cellValue))
                            : String(cellValue).toLowerCase()
                        const filterValueNormalized = isLocationColumn
                            ? normalizeForSearch(filterValue as string)
                            : (filterValue as string)
                        if (!cellValueLower.includes(filterValueNormalized)) return false
                    } catch (error) {
                        console.error('Error processing filter for column:', column.key, error)
                        return false
                    }
                }

                return true
            })

            // Sort filtered results
            const sorted = sortOrders(filtered, sortConfig)
            return sorted
        } catch (error) {
            console.error('Error filtering orders:', error)
            return []
        }
    }, [orders, deferredFilters, sortConfig])

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo<OrdersContextValue>(
        () => ({
            orders,
            filteredOrders,
            isLoading,
            isRefetching,
            lastUpdated,
            filters,
            activeFilters,
            setFilters,
            handleFilterChange,
            handleClearFilter,
            handleClearAllFilters,
            getFilterDisplayValue,
            sortConfig,
            handleSortAsc,
            handleSortDesc,
            handleRefresh,
        }),
        [
            orders,
            filteredOrders,
            isLoading,
            isRefetching,
            lastUpdated,
            filters,
            activeFilters,
            setFilters,
            handleFilterChange,
            handleClearFilter,
            handleClearAllFilters,
            getFilterDisplayValue,
            sortConfig,
            handleSortAsc,
            handleSortDesc,
            handleRefresh,
        ],
    )

    return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>
}
